import { mapGetters } from 'vuex';

/**
 * A Vue.js component to render an image that can be zoomed
 * by clicking then moving the cursor around, or by tapping
 * and dragging on mobile.
 *
 * @param {String} src The src of the original image
 * @param {String} size Determines the original image size to retrive from the server
 * @param {String} alt Alt text to represent the image
 * @param {String} css CSS classes to add to the parent
 * @param {String} fit "cover" or "contain"
 * @param {String} pos "center", "top" or "bottom"
 * @param {Boolean} lazy Should the image load in lazily
 * @param {Boolean} transition Should there be a fade in transition?
 * @param {Boolean} transparent Should the image have a transparent background?
 *
 * @example
 * <div
 *   is="vue-image-zoom"
 *   :src="imageSrc"
 *   :size="650"
 *   :alt="title"
 *   css="product-card__image"
 *   fit="cover"
 *   pos="bottom"
 *   lazy="true"
 *   transition="true"
 *   transparent="true" />
 */
export default {
  components: {
    'vue-image': () => import('../vue-image/vue-image.vue'),
    'vue-image-v2': () => import('../vue-image-v2/vue-image-v2.vue'),
  },
  props: [
    'src',
    'size',
    'alt',
    'css',
    'fit',
    'pos',
    'lazy',
    'transition',
    'transparent',
  ],
  data() {
    return {
      image: {
        active: false,
        src: '',
        posX: 0,
        posY: 0,
        wrapper: {
          top: 0,
          left: 0,
        },
        width: 0,
        height: 0,
      },
    };
  },
  computed: {
    ...mapGetters('product', ['productTitle']),
  },
  methods: {
    handleZoomTracking() {
      dataLayer.push({
        event: 'product_module_interaction',
        click_action: 'zoom',
        product_name: this.productTitle,
        module_name: 'product gallery',
        shopify_module_name: 'vue image zoom'
      });
    },
    replaceImage(event, string) {
      if (this.image.active) {
        this.image.active = false;
      } else {
        this.zoomImage(event, string);
      }
      this.handleZoomTracking();
    },
    zoomImage(event, string) {
      const img = new Image();
      img.src = string;
      img.onload = this.openZoom(event, string);
    },
    openZoom(event, string) {
      this.image.active = true;
      this.image.src = string;
      this.position(event);
    },
    measuring() {
      this.image.wrapper = this.$refs.imageWrapper.getBoundingClientRect();
      this.image.width = this.$refs.imageWrapper.clientWidth;
      this.image.height = this.$refs.imageWrapper.clientHeight;
    },
    position(event) {
      this.measuring(); // Can't be on mounted cause need to calculate because of window scroll or gallery chaged
      const x = event.clientX - this.image.wrapper.left;
      const y = event.clientY - this.image.wrapper.top;
      this.image.posX = this.image.width / 2 - x;
      this.image.posY = this.image.height / 2 - y;
    },
  },
  template: `
      <div
        ref="imageWrapper"
        :class="{ active: image.active }"
        class="product-gallery__zoom relative fill"
        @click.prevent="replaceImage($event, src)">
        <div
          v-if="image.active"
          class="product-gallery__zoom__zoomed absolute z10"
          :style="{ backgroundImage: 'url(' + image.src + ')', transform: 'translate('+image.posX +'px,'+ image.posY +'px)' }"
          @mousemove="position" @mouseleave="image.active=false" />
        <vue-image-v2
          :src="src"
          :size="size"
          :alt="alt"
          :css="css"
          :fit="fit"
          :pos="pos"
          :lazy="false"
          :transition="transition"
          :transparent="transparent" />
      </div>
    `,
};
